import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Navbar, Footer } from '@/components/Layout';
import {
  TrendingUp, DollarSign, Users, Activity, BarChart3, Shield,
  Coins, Bot, MessageSquare, Vote, ExternalLink, ArrowRight,
  Layers, Flame, FileText, Zap, Globe, Clock, ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

function StatCard({ icon: Icon, label, value, sub, color, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="p-5 rounded-2xl bg-white/[0.03] border border-white/5 hover:border-white/10 transition-all group"
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        {sub && <span className="text-xs text-gray-500">{sub}</span>}
      </div>
      <div className="text-2xl font-bold text-white mb-0.5">{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </motion.div>
  );
}

function MiniBar({ data, label, color = 'bg-blue-500' }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <div>
      <div className="text-xs text-gray-500 mb-2">{label}</div>
      <div className="flex items-end gap-[3px] h-20">
        {data.slice(-14).map((d, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <div
              className={`w-full ${color} rounded-t transition-all opacity-80 hover:opacity-100`}
              style={{ height: `${Math.max((d.count / max) * 100, 4)}%` }}
              title={`${d.date}: ${d.count}`}
            />
          </div>
        ))}
      </div>
      {data.length > 1 && (
        <div className="flex justify-between mt-1 text-[9px] text-gray-600">
          <span>{data[Math.max(0, data.length - 14)]?.date?.slice(5)}</span>
          <span>{data[data.length - 1]?.date?.slice(5)}</span>
        </div>
      )}
    </div>
  );
}

export default function Analytics() {
  const [data, setData] = useState(null);
  const [market, setMarket] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/analytics/public`).then(r => r.json()),
      fetch(`${API}/api/token/market`).then(r => r.json()),
    ]).then(([analytics, mkt]) => {
      setData(analytics);
      setMarket(mkt);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const fmt = (n) => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return n?.toLocaleString?.() ?? n;
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="analytics-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-10 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 mb-8"
          >
            <Activity className="w-4 h-4 text-blue-400" />
            <span className="text-sm text-blue-300">Live Platform Data</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight mb-5"
          >
            Platform{' '}
            <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-teal-400 bg-clip-text text-transparent">
              Analytics
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto"
          >
            Real-time metrics from the Kojiru ecosystem. All data pulled live from on-chain contracts and our platform database.
          </motion.p>
        </div>
      </section>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : data ? (
        <>
          {/* Primary Metrics */}
          <section className="py-8 px-6">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="primary-metrics">
                <StatCard
                  icon={Users} label="Total Users" value={fmt(data.users.total)}
                  sub={`${data.users.consumers}C / ${data.users.lenders}L`}
                  color="bg-blue-500/10 text-blue-400" delay={0}
                />
                <StatCard
                  icon={DollarSign} label="Loan Volume" value={`$${fmt(data.loans.loan_volume)}`}
                  sub={`${data.loans.total_loans} funded`}
                  color="bg-green-500/10 text-green-400" delay={0.05}
                />
                <StatCard
                  icon={FileText} label="Loan Requests" value={fmt(data.loans.total_requests)}
                  sub={`$${fmt(data.loans.request_volume)} total`}
                  color="bg-purple-500/10 text-purple-400" delay={0.1}
                />
                <StatCard
                  icon={Layers} label="Total Bids" value={fmt(data.loans.total_bids)}
                  sub={`${data.loans.active_requests} open RFBs`}
                  color="bg-orange-500/10 text-orange-400" delay={0.15}
                />
              </div>
            </div>
          </section>

          {/* Token Market Overview */}
          {market && (
            <section className="py-8 px-6">
              <div className="max-w-6xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="rounded-2xl border border-yellow-500/10 bg-gradient-to-br from-yellow-500/[0.03] to-orange-500/[0.02] overflow-hidden"
                  data-testid="token-market-overview"
                >
                  {/* Header */}
                  <div className="p-5 border-b border-white/5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center">
                        <Coins className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h2 className="text-base font-semibold">KOJIRU Token</h2>
                          <span className="px-1.5 py-0.5 rounded bg-yellow-500/10 text-yellow-400 text-[9px] font-bold uppercase tracking-wider">KOJI</span>
                        </div>
                        <p className="text-[10px] text-gray-500">Base Mainnet (ERC-20)</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="px-2 py-1 rounded-full bg-white/5 text-[10px] text-gray-400 border border-white/5">
                        Base Mainnet (ERC-20)
                      </span>
                    </div>
                  </div>

                  <div className="p-5">
                    {/* Price + Key Stats Row */}
                    <div className="flex flex-col md:flex-row gap-6 mb-6">
                      {/* Price */}
                      <div className="flex-shrink-0">
                        <div className="text-3xl font-bold text-white tracking-tight" data-testid="token-price">
                          {market.price_formatted}
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          <span
                            className={`flex items-center gap-1 text-sm font-medium ${
                              market.change_24h >= 0 ? 'text-green-400' : 'text-red-400'
                            }`}
                            data-testid="token-change-24h"
                          >
                            {market.change_24h >= 0 ? (
                              <TrendingUp className="w-3.5 h-3.5" />
                            ) : (
                              <TrendingUp className="w-3.5 h-3.5 rotate-180" />
                            )}
                            {market.change_24h >= 0 ? '+' : ''}{market.change_24h}%
                            <span className="text-gray-600 text-xs">24h</span>
                          </span>
                          <span
                            className={`text-xs font-medium ${
                              market.change_7d >= 0 ? 'text-green-400' : 'text-red-400'
                            }`}
                            data-testid="token-change-7d"
                          >
                            {market.change_7d >= 0 ? '+' : ''}{market.change_7d}%
                            <span className="text-gray-600 ml-0.5">7d</span>
                          </span>
                        </div>
                      </div>

                      {/* Mini stats */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 flex-1">
                        <div className="p-3 rounded-xl bg-white/[0.03] border border-white/5">
                          <div className="text-[10px] text-gray-500 mb-1">Market Cap</div>
                          <div className="text-sm font-bold text-white">${fmt(market.market_cap)}</div>
                        </div>
                        <div className="p-3 rounded-xl bg-white/[0.03] border border-white/5">
                          <div className="text-[10px] text-gray-500 mb-1">24h Volume</div>
                          <div className="text-sm font-bold text-white">${fmt(market.volume_24h)}</div>
                        </div>
                        <div className="p-3 rounded-xl bg-white/[0.03] border border-white/5">
                          <div className="text-[10px] text-gray-500 mb-1">FDV</div>
                          <div className="text-sm font-bold text-white">${fmt(market.fully_diluted_valuation)}</div>
                        </div>
                        <div className="p-3 rounded-xl bg-white/[0.03] border border-white/5">
                          <div className="text-[10px] text-gray-500 mb-1">Holders</div>
                          <div className="text-sm font-bold text-white">{market.holders}</div>
                        </div>
                      </div>
                    </div>

                    {/* 30-Day Price Chart */}
                    {market.price_history?.length > 0 && (
                      <div data-testid="price-chart">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs text-gray-500">Price (30 Days)</span>
                          <div className="flex items-center gap-3 text-[10px] text-gray-600">
                            <span>Low: ${Math.min(...market.price_history.map(p => p.price)).toFixed(4)}</span>
                            <span>High: ${Math.max(...market.price_history.map(p => p.price)).toFixed(4)}</span>
                          </div>
                        </div>
                        <div className="relative h-32 w-full">
                          <svg viewBox="0 0 600 120" className="w-full h-full" preserveAspectRatio="none">
                            {/* Grid lines */}
                            {[0, 30, 60, 90, 120].map(y => (
                              <line key={y} x1="0" y1={y} x2="600" y2={y} stroke="rgba(255,255,255,0.03)" />
                            ))}
                            {(() => {
                              const prices = market.price_history.map(p => p.price);
                              const min = Math.min(...prices);
                              const max = Math.max(...prices);
                              const range = max - min || 0.0001;
                              const points = prices.map((p, i) => {
                                const x = (i / (prices.length - 1)) * 600;
                                const y = 115 - ((p - min) / range) * 110;
                                return `${x},${y}`;
                              }).join(' ');
                              const isPositive = prices[prices.length - 1] >= prices[0];
                              const lineColor = isPositive ? '#22c55e' : '#ef4444';
                              const gradId = 'priceGrad';
                              return (
                                <>
                                  <defs>
                                    <linearGradient id={gradId} x1="0%" y1="0%" x2="0%" y2="100%">
                                      <stop offset="0%" stopColor={lineColor} stopOpacity="0.15" />
                                      <stop offset="100%" stopColor={lineColor} stopOpacity="0" />
                                    </linearGradient>
                                  </defs>
                                  <polygon
                                    points={`0,120 ${points} 600,120`}
                                    fill={`url(#${gradId})`}
                                  />
                                  <polyline
                                    points={points}
                                    fill="none"
                                    stroke={lineColor}
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  {/* Current price dot */}
                                  {(() => {
                                    const lastX = 600;
                                    const lastY = 115 - ((prices[prices.length - 1] - min) / range) * 110;
                                    return (
                                      <circle cx={lastX} cy={lastY} r="3" fill={lineColor} stroke="#050505" strokeWidth="1.5" />
                                    );
                                  })()}
                                </>
                              );
                            })()}
                          </svg>
                        </div>
                        <div className="flex justify-between mt-1 text-[9px] text-gray-600">
                          <span>{market.price_history[0]?.date?.slice(5)}</span>
                          <span>{market.price_history[Math.floor(market.price_history.length / 2)]?.date?.slice(5)}</span>
                          <span>Today</span>
                        </div>
                      </div>
                    )}

                    {/* Supply breakdown bar */}
                    <div className="mt-5 pt-4 border-t border-white/5">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-gray-500">Supply Distribution</span>
                        <span className="text-[10px] text-gray-600">Max Supply: 1B KOJIRU</span>
                      </div>
                      <div className="h-3 rounded-full bg-white/5 overflow-hidden flex">
                        <div
                          className="bg-blue-500 transition-all"
                          style={{ width: `${(market.circulating_supply / market.max_supply) * 100}%` }}
                          title={`Circulating: ${fmt(market.circulating_supply)}`}
                        />
                        <div
                          className="bg-green-500 transition-all"
                          style={{ width: `${(market.total_staked / market.max_supply) * 100}%` }}
                          title={`Staked: ${fmt(market.total_staked)}`}
                        />
                        <div
                          className="bg-red-500 transition-all"
                          style={{ width: `${Math.max((market.total_burned / market.max_supply) * 100, 0.1)}%` }}
                          title={`Burned: ${fmt(market.total_burned)}`}
                        />
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-[10px] text-gray-500">
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" /> Circulating ({fmt(market.circulating_supply)})</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" /> Staked ({fmt(market.total_staked)})</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> Burned ({fmt(market.total_burned)})</span>
                        <span className="ml-auto text-gray-600">Unminted: {fmt(market.max_supply - market.total_supply)}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </section>
          )}

          {/* On-Chain Stats */}
          <section className="py-8 px-6">
            <div className="max-w-6xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="rounded-2xl border border-cyan-500/10 bg-gradient-to-br from-cyan-500/[0.03] to-blue-500/[0.02] p-6"
                data-testid="on-chain-stats"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                    <Globe className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold">On-Chain Metrics</h2>
                    <p className="text-[10px] text-gray-500">Live data from Base Mainnet smart contracts</p>
                  </div>
                  <a
                    href="https://base.org"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-auto flex items-center gap-1.5 text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
                  >
                    Base Mainnet <ExternalLink className="w-3 h-3" />
                  </a>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                  <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5 text-center">
                    <Coins className="w-5 h-5 text-yellow-400 mx-auto mb-2" />
                    <div className="text-lg font-bold text-white">{fmt(parseFloat(data.on_chain.total_supply))}</div>
                    <div className="text-[10px] text-gray-500">Total Supply</div>
                  </div>
                  <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5 text-center">
                    <Flame className="w-5 h-5 text-red-400 mx-auto mb-2" />
                    <div className="text-lg font-bold text-red-400">{fmt(parseFloat(data.on_chain.total_burned))}</div>
                    <div className="text-[10px] text-gray-500">Total Burned</div>
                  </div>
                  <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5 text-center">
                    <Shield className="w-5 h-5 text-green-400 mx-auto mb-2" />
                    <div className="text-lg font-bold text-green-400">{fmt(parseFloat(data.on_chain.total_staked))}</div>
                    <div className="text-[10px] text-gray-500">Total Staked</div>
                  </div>
                  <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5 text-center">
                    <Coins className="w-5 h-5 text-purple-400 mx-auto mb-2" />
                    <div className="text-lg font-bold text-purple-400">{fmt(parseFloat(data.on_chain.reward_pool))}</div>
                    <div className="text-[10px] text-gray-500">Reward Pool</div>
                  </div>
                  <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5 text-center">
                    <TrendingUp className="w-5 h-5 text-blue-400 mx-auto mb-2" />
                    <div className="text-lg font-bold text-blue-400">{fmt(parseFloat(data.on_chain.rewards_distributed))}</div>
                    <div className="text-[10px] text-gray-500">Rewards Paid</div>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-4 text-[10px] text-gray-500">
                  <span className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Base Mainnet
                  </span>
                  <span>Chain ID: {data.token.chain_id}</span>
                </div>
              </motion.div>
            </div>
          </section>

          {/* Community + Governance + Growth */}
          <section className="py-8 px-6">
            <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-6">
              {/* Community */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="rounded-2xl border border-white/5 bg-white/[0.02] p-5"
                data-testid="community-stats"
              >
                <div className="flex items-center gap-2 mb-5">
                  <Bot className="w-5 h-5 text-blue-400" />
                  <h3 className="text-sm font-semibold">AI & Community</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Agent Ratings</span>
                    <span className="text-sm font-bold text-white">{data.community.total_feedback}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Unique Raters</span>
                    <span className="text-sm font-bold text-white">{data.community.unique_raters}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Total Referrals</span>
                    <span className="text-sm font-bold text-white">{data.community.total_referrals}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">AI Agents Live</span>
                    <span className="text-sm font-bold text-green-400">2</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Trust Standard</span>
                    <span className="text-sm font-bold text-cyan-400">ERC-8004</span>
                  </div>
                </div>
              </motion.div>

              {/* Governance */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.05 }}
                className="rounded-2xl border border-white/5 bg-white/[0.02] p-5"
                data-testid="governance-stats"
              >
                <div className="flex items-center gap-2 mb-5">
                  <Vote className="w-5 h-5 text-purple-400" />
                  <h3 className="text-sm font-semibold">Governance</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Total Proposals</span>
                    <span className="text-sm font-bold text-white">{data.governance.total_proposals}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Active Proposals</span>
                    <span className="text-sm font-bold text-green-400">{data.governance.active_proposals}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Governance Model</span>
                    <span className="text-sm font-bold text-purple-400">DAO</span>
                  </div>
                </div>
                <Link to="/governance" className="mt-4 block">
                  <Button variant="outline" size="sm" className="w-full border-white/10 text-xs gap-1">
                    View Proposals <ChevronRight className="w-3 h-3" />
                  </Button>
                </Link>
              </motion.div>

              {/* Growth Charts */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="rounded-2xl border border-white/5 bg-white/[0.02] p-5"
                data-testid="growth-charts"
              >
                <div className="flex items-center gap-2 mb-5">
                  <BarChart3 className="w-5 h-5 text-green-400" />
                  <h3 className="text-sm font-semibold">Growth (30d)</h3>
                </div>
                <div className="space-y-5">
                  <MiniBar data={data.growth.users} label="New Users / Day" color="bg-blue-500" />
                  <MiniBar data={data.growth.loans} label="Loan Requests / Day" color="bg-green-500" />
                </div>
              </motion.div>
            </div>
          </section>

          {/* Recent Activity */}
          {data.recent_activity?.length > 0 && (
            <section className="py-8 px-6">
              <div className="max-w-6xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="rounded-2xl border border-white/5 bg-white/[0.02] overflow-hidden"
                  data-testid="recent-activity"
                >
                  <div className="p-5 border-b border-white/5 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-gray-400" />
                    <h3 className="text-sm font-semibold">Recent Platform Activity</h3>
                  </div>
                  <div className="divide-y divide-white/[0.04]">
                    {data.recent_activity.map((event, i) => (
                      <div key={i} className="flex items-center gap-4 px-5 py-3 hover:bg-white/[0.01] transition-colors">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          event.type === 'Loan Funded' ? 'bg-green-500/10 text-green-400' : 'bg-blue-500/10 text-blue-400'
                        }`}>
                          {event.type === 'Loan Funded' ? <DollarSign className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium">{event.type}</span>
                          {event.detail && <span className="text-xs text-gray-500 ml-2">{event.detail}</span>}
                        </div>
                        <div className="text-sm font-bold text-white">${fmt(event.amount)}</div>
                        <div className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                          event.status === 'funded' ? 'bg-green-500/10 text-green-400' : 'bg-yellow-500/10 text-yellow-400'
                        }`}>
                          {event.status}
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </section>
          )}

          {/* Contract Info */}
          <section className="py-8 px-6">
            <div className="max-w-6xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="rounded-2xl border border-white/5 bg-white/[0.02] p-6"
                data-testid="contract-addresses"
              >
                <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-cyan-400" /> Smart Contracts
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl bg-white/[0.02] border border-white/5">
                    <div className="text-xs text-gray-500 mb-1">KOJIRU Token (ERC-20)</div>
                    <span className="text-sm text-gray-400">Deployed & Verified on Base Mainnet</span>
                  </div>
                  <div className="p-4 rounded-xl bg-white/[0.02] border border-white/5">
                    <div className="text-xs text-gray-500 mb-1">Staking Contract</div>
                    <span className="text-sm text-gray-400">Deployed & Verified on Base Mainnet</span>
                  </div>
                </div>
                <p className="text-[10px] text-gray-600 mt-3">Contract addresses will be published at official launch.</p>
              </motion.div>
            </div>
          </section>

          {/* Last Updated */}
          <section className="pb-8 px-6">
            <div className="max-w-6xl mx-auto text-center">
              <p className="text-[10px] text-gray-600">
                Last updated: {new Date(data.last_updated).toLocaleString()} | Data refreshes every 2 minutes
              </p>
            </div>
          </section>
        </>
      ) : (
        <div className="text-center py-20 text-gray-500">Failed to load analytics data</div>
      )}

      {/* CTA */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">Ready to Join the Ecosystem?</h2>
          <p className="text-sm text-gray-400 mb-8">
            Stake, govern, and earn KOJIRU tokens in the Agent Credit Protocol on Base.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Link to="/auth">
              <Button size="lg" className="bg-gradient-to-r from-blue-500 to-cyan-500 gap-2" data-testid="analytics-cta">
                Get Started <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/tokenomics">
              <Button size="lg" variant="outline" className="border-white/10 gap-2">
                Tokenomics <ChevronRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
